import { useEffect } from "react";

/**
 * useMetaDescription — bulletproof per-page <meta name="description"> setter.
 *
 * Mirrors useCanonical's pattern. react-helmet has an unreliable meta-override
 * behaviour in this codebase: the static <meta name="description"> from
 * public/index.html is not replaced at runtime by Helmet, so the SEO
 * descriptions written in <Helmet> blocks never reach Googlebot or the
 * social preview crawlers.
 *
 * This hook imperatively updates the FIRST <meta name="description"> tag
 * in <head> on mount and restores it back to the original on unmount, so
 * SPA navigation across pages cleanly carries the right description per route.
 *
 * Usage:
 *   useMetaDescription("Your concise 120-160 char description.");
 */
export default function useMetaDescription(description) {
  useEffect(() => {
    if (!description) return undefined;
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (created) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const previous = meta.getAttribute("content");
    meta.setAttribute("content", description);

    return () => {
      // Restore previous description so the next route can set its own
      // without leaking this page's description.
      if (meta && meta.parentNode) {
        if (previous !== null) {
          meta.setAttribute("content", previous);
        }
      }
    };
  }, [description]);
}
